import { DictionaryTerm } from 'src/app/interfaces/dictionary';
import { dictionaryReadableParagraphs } from 'src/app/services/geo-readable-text.util';
import { formatSeoTitle } from 'src/app/services/seo.defaults';
import {
  buildDefinedTermJsonLd,
  buildDefinedTermSetJsonLd,
} from 'src/app/services/seo-json-ld.util';
import { PageSeo } from 'src/app/services/seo.model';
import { SeoService } from 'src/app/services/seo.service';

const DESC_MAX = 320;
const INDEX_DESCRIPTION =
  'Definitions and explanations of blockchain terms, curated on ChainMart.';

function clip(value: string, max: number): string {
  const text = value.replace(/\s+/g, ' ').trim();
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max - 1).trim()}…`;
}

export function dictionaryTermPath(slug: string): string {
  return `/dictionary/${encodeURIComponent(slug.trim().toLowerCase())}`;
}

export function buildDictionaryIndexSeo(terms: DictionaryTerm[]): Partial<PageSeo> {
  return {
    title: formatSeoTitle('Blockchain A–Z'),
    description: INDEX_DESCRIPTION,
    ogDescription: 'Blockchain A–Z glossary on ChainMart.',
    jsonLd: buildDefinedTermSetJsonLd({
      name: 'ChainMart Blockchain A–Z',
      description: INDEX_DESCRIPTION,
      url: '',
      terms: terms
        .filter((term) => term.slug?.trim() && term.term?.trim())
        .slice(0, 1000)
        .map((term) => ({
          name: term.term.trim(),
          description: term.definition?.trim()
            ? clip(term.definition, DESC_MAX)
            : undefined,
          url: dictionaryTermPath(term.slug),
        })),
    }),
  };
}

export function buildDictionaryTermSeo(term: DictionaryTerm): Partial<PageSeo> {
  const paragraphs = dictionaryReadableParagraphs(term);
  const description =
    clip(paragraphs.join(' '), DESC_MAX) ||
    `Definition of ${term.term} on ChainMart.`;
  return {
    title: formatSeoTitle(term.term?.trim() || 'Dictionary term'),
    description,
    ogDescription: description,
    jsonLd: buildDefinedTermJsonLd({
      name: term.term?.trim() || 'Dictionary term',
      description: paragraphs.join('\n\n'),
      url: '',
    }),
  };
}

const DICTIONARY_INDEX_PATH = '/dictionary';

export function applyDictionaryIndexSeo(seoService: SeoService, terms: DictionaryTerm[]): void {
  seoService.applyPageOverride(buildDictionaryIndexSeo(terms), DICTIONARY_INDEX_PATH);
}

export function applyDictionaryTermSeo(seoService: SeoService, term: DictionaryTerm): void {
  const slug = term.slug?.trim().toLowerCase();
  seoService.applyPageOverride(
    buildDictionaryTermSeo(term),
    slug ? dictionaryTermPath(slug) : DICTIONARY_INDEX_PATH,
  );
}
